import { getDirectoryUsersByIdsOptions } from "@/lib/data/react-query/firm/firm-user";
import { useQuery } from "@tanstack/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function fieldLabel(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (first) => first.toUpperCase());
}

function Fields({ value }: { value: unknown }) {
  if (value === null) return <span>Not set</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (Array.isArray(value)) {
    return value.length ? (
      <ul className="list-disc space-y-1 pl-4">
        {value.map((entry, index) => (
          <li key={index}>
            <Fields value={entry} />
          </li>
        ))}
      </ul>
    ) : (
      <span>None</span>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(record(value));
    return entries.length ? (
      <dl className="space-y-2">
        {entries.map(([key, child]) => (
          <div key={key}>
            <dt className="text-muted-foreground text-xs">{fieldLabel(key)}</dt>
            <dd className="break-words whitespace-pre-wrap">
              <Fields value={child} />
            </dd>
          </div>
        ))}
      </dl>
    ) : (
      <span>None</span>
    );
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "bigint" ||
    typeof value === "symbol"
  ) {
    return <span>{String(value)}</span>;
  }
  return <span>Not set</span>;
}

export function ChatApprovalAction({
  action,
  commandId,
}: {
  commandId?: string;
  action: Record<string, unknown>;
}) {
  const isNote = commandId === "matters.create_matter_note";
  const target = record(action.target);
  const changes = record(action.changes);
  const matterId =
    typeof target.matterId === "string"
      ? target.matterId
      : typeof changes.matterId === "string"
        ? changes.matterId
        : "";
  const { data } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    retry: false,
  });
  const matter = data?.data;
  const title =
    matter && !Array.isArray(matter) && typeof matter.title === "string"
      ? matter.title
      : undefined;
  const authorId =
    isNote && typeof changes.userId === "string" ? changes.userId : "";
  const { data: authors } = useQuery(
    getDirectoryUsersByIdsOptions(authorId ? [authorId] : []),
  );
  const author = authors?.find((user) => user.id === authorId);
  const authorName = author
    ? [author.firstName, author.lastName].filter(Boolean).join(" ")
    : undefined;
  const changeFields = Object.fromEntries(
    Object.entries(changes).filter(
      ([key]) =>
        (key !== "matterId" || !matterId) &&
        !(isNote && ["message", "userId"].includes(key)),
    ),
  );
  const otherTarget = Object.fromEntries(
    Object.entries(target).filter(([key]) => key !== "matterId"),
  );
  return (
    <div className="space-y-3 text-sm">
      {typeof action.summary === "string" && (
        <p className="font-medium">{isNote ? "Add a note" : action.summary}</p>
      )}
      {matterId && (
        <p>
          Case:{" "}
          <a
            className="text-primary underline underline-offset-2"
            href={`/matters/${encodeURIComponent(matterId)}`}
          >
            {title || "Open case"}
          </a>
        </p>
      )}
      {isNote && authorId && <p>Author: {authorName || `User ${authorId}`}</p>}
      {isNote && changes.message !== undefined && (
        <div>
          <p className="text-muted-foreground text-xs">Note</p>
          <div className="break-words whitespace-pre-wrap">
            <Fields value={changes.message} />
          </div>
        </div>
      )}
      {Object.keys(otherTarget).length > 0 && <Fields value={otherTarget} />}
      {Object.keys(changeFields).length > 0 && <Fields value={changeFields} />}
      {action.changes !== undefined &&
        !Object.keys(changes).length &&
        (typeof action.changes !== "object" ||
          Array.isArray(action.changes) ||
          action.changes === null) && <Fields value={action.changes} />}
      {Object.keys(record(action.filters)).length > 0 && (
        <Fields value={action.filters} />
      )}
    </div>
  );
}
